import React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";

import { routes } from "@/editor/utils/router";
import { errorToast } from "@replo/design-system/components/alert/Toast";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import PageHeader from "@replo/design-system/components/PageHeader";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  ALargeSmall,
  Code2,
  HeartHandshake,
  Puzzle,
  Receipt,
  Settings2,
  TestTubeDiagonal,
  Users,
} from "lucide-react";
import {
  generatePath,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const baseShopSidebarItems = [
  {
    label: "General",
    path: routes.shops.settings.shops.general,
    icon: Settings2,
  },
  {
    label: "Fonts",
    path: routes.shops.settings.shops.fonts,
    icon: ALargeSmall,
  },
  {
    label: "A/B Testing",
    path: routes.shops.settings.shops.abtesting,
    icon: TestTubeDiagonal,
  },
];

const scriptManagerItem = {
  label: "Script Manager",
  path: routes.shops.settings.shops.scripts,
  icon: Code2,
};

const integrationsItem = {
  label: "Integrations",
  path: routes.shops.settings.shops.integrations.home,
  icon: Puzzle,
};

const getShopSidebarItems = (isReploHostedProject: boolean) => {
  return [
    ...baseShopSidebarItems,
    ...(isReploHostedProject ? [scriptManagerItem] : []),
    integrationsItem,
  ];
};

const workspaceSidebarItems = [
  {
    label: "General",
    path: routes.shops.settings.workspace.general,
    icon: Settings2,
  },
  {
    label: "Members",
    path: routes.shops.settings.workspace.members,
    icon: Users,
  },
  {
    label: "Billing",
    path: routes.shops.settings.workspace.billing,
    icon: Receipt,
  },
  {
    label: "Affiliates",
    path: routes.shops.settings.workspace.affiliates,
    icon: HeartHandshake,
  },
];

const workspaceOnlySidebarItems = [
  {
    label: "General",
    path: routes.workspace.settings.general,
    icon: Settings2,
  },
  {
    label: "Members",
    path: routes.workspace.settings.members,
    icon: Users,
  },
  {
    label: "Billing",
    path: routes.workspace.settings.billing,
    icon: Receipt,
  },
  {
    label: "Affiliates",
    path: routes.workspace.settings.affiliates,
    icon: HeartHandshake,
  },
];

const isItemSelected = (path: string) => {
  return location.pathname.startsWith(path);
};

const IconContainer = ({ children }: React.PropsWithChildren) => (
  <div className="flex h-5 w-5 items-center justify-center rounded-sm bg-surface">
    {children}
  </div>
);

const ShopSettings = () => {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();
  const location = useLocation();
  const { userRole, isLoading, project } = useCurrentProjectContext();

  // Check if we're in workspace-only mode (no project context)
  const isWorkspaceOnly = !project && !projectId;

  // Only check access after loading is complete
  const canViewWorkspace = React.useMemo(() => {
    // If still loading, don't make access decisions yet
    if (isLoading) {
      return undefined;
    }

    return exhaustiveSwitch({ type: userRole ?? "none" })({
      superuser: () => true,
      workspaceOwner: () => true,
      workspaceMember: () => true,
      projectMember: () => false,
      none: () => false,
    });
  }, [userRole, isLoading]);

  // Handle redirect for unauthorized access to workspace settings
  React.useEffect(() => {
    // Only redirect if we're not loading and the user doesn't have access
    if (
      !isLoading &&
      canViewWorkspace === false &&
      location.pathname.includes("/settings/workspace")
    ) {
      errorToast(
        "You do not have access to this workspace.",
        "Please contact the workspace admin to gain access.",
      );

      navigate(
        generatePath(routes.shops.settings.shops.general, {
          projectId,
        }),
        {
          replace: true,
        },
      );
    }
  }, [canViewWorkspace, isLoading, location.pathname, projectId, navigate]);

  // Choose the appropriate sidebar items based on context
  const workspaceItems = isWorkspaceOnly
    ? workspaceOnlySidebarItems
    : workspaceSidebarItems;

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <PageHeader title="Settings" />
      <div className="flex pt-6 w-full overflow-hidden">
        <div className="w-[160px] space-y-2">
          {/* Only show Shop section when not in workspace-only mode */}
          {!isWorkspaceOnly && (
            <>
              <div className="header-h5">Shop</div>
              <div
                className={twMerge(
                  "border-b-0.5 border-border pb-2",
                  !canViewWorkspace && "border-b-0 pb-0",
                )}
              >
                {getShopSidebarItems(project?.hostingType === "replo").map(
                  (item) => (
                    <MenuItem
                      variant="custom"
                      startEnhancer={
                        <IconContainer>
                          <item.icon size={12} />
                        </IconContainer>
                      }
                      key={item.path}
                      onClick={() => {
                        if (projectId) {
                          navigate(generatePath(item.path, { projectId }));
                        }
                      }}
                      size="sm"
                      layoutClassName="h-8"
                      selected={
                        isWorkspaceOnly
                          ? isItemSelected(
                              generatePath(item.path, { workspaceId }),
                            )
                          : isItemSelected(
                              generatePath(item.path, { projectId }),
                            )
                      }
                    >
                      {item.label}
                    </MenuItem>
                  ),
                )}
              </div>
            </>
          )}
          {/* Show workspace section when we have access or in workspace-only mode */}
          {/* Don't show workspace section while loading unless in workspace-only mode */}
          {((!isLoading && canViewWorkspace) || isWorkspaceOnly) && (
            <>
              <div className="header-h5">Workspace</div>
              <div>
                {workspaceItems.map((item) => (
                  <MenuItem
                    variant="custom"
                    startEnhancer={
                      <IconContainer>
                        <item.icon size={12} />
                      </IconContainer>
                    }
                    key={item.path}
                    onClick={() => {
                      if (isWorkspaceOnly && workspaceId) {
                        navigate(generatePath(item.path, { workspaceId }));
                      } else if (projectId) {
                        navigate(generatePath(item.path, { projectId }));
                      }
                    }}
                    size="sm"
                    layoutClassName="h-8"
                    selected={isItemSelected(
                      isWorkspaceOnly && workspaceId
                        ? generatePath(item.path, { workspaceId })
                        : generatePath(item.path, { projectId }),
                    )}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex-1 px-6 overflow-y-auto justify-center flex h-full">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default ShopSettings;
